/* ============================================
   CUSTOM: Transform Add-to-Cart button into
   green quantity selector when item is in cart
   Added: April 2026
   Stage 2: Red → Green quantity transformation

   Updated May 2026: transformButton now skips hidden buttons
   (offsetParent === null). card-product-list.liquid renders TWO
   .product-card__atc buttons per card — a mobile icon-only one
   and a desktop text one, toggled by md: classes. Transforming
   both produced duplicate green selectors in the cart drawer.
   ============================================ */

(function () {
  'use strict';

  // Wait for the theme's FoxTheme object to be available
  function waitForFoxTheme(callback) {
    if (window.FoxTheme && window.FoxTheme.pubsub) {
      callback();
    } else {
      setTimeout(function () {
        waitForFoxTheme(callback);
      }, 100);
    }
  }

  // Fetch current cart state from Shopify
  async function fetchCart() {
    try {
      const response = await fetch('/cart.js', {
        headers: { 'Accept': 'application/json' }
      });
      return await response.json();
    } catch (error) {
      console.error('[CustomATC] Error fetching cart:', error);
      return null;
    }
  }

  // Build a map of variantId -> quantity currently in cart
  function buildCartQuantityMap(cart) {
    const map = new Map();
    if (!cart || !cart.items) return map;
    cart.items.forEach(function (item) {
      const existing = map.get(item.variant_id) || 0;
      map.set(item.variant_id, existing + item.quantity);
    });
    return map;
  }

  // Build the green quantity selector HTML
  function buildQuantitySelector(variantId, quantity, originalButton) {
    const wrapper = document.createElement('div');
    wrapper.className = 'custom-atc-qty-selector';
    wrapper.setAttribute('data-variant-id', variantId);

    wrapper.innerHTML =
      '<button type="button" class="custom-atc-qty-btn custom-atc-qty-minus" aria-label="Decrease quantity">−</button>' +
      '<span class="custom-atc-qty-value">' + quantity + '</span>' +
      '<button type="button" class="custom-atc-qty-btn custom-atc-qty-plus" aria-label="Increase quantity">+</button>';

    // Copy sizing classes from original button so layout stays consistent
    if (originalButton) {
      const classes = originalButton.className.split(' ');
      classes.forEach(function (cls) {
        if (cls.indexOf('w-full') === 0 || cls.indexOf('product-card__list-atc') === 0) {
          wrapper.classList.add(cls);
        }
      });
    }

    // Hook up the − button
    wrapper.querySelector('.custom-atc-qty-minus').addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      changeQuantity(variantId, -1, wrapper);
    });

    // Hook up the + button
    wrapper.querySelector('.custom-atc-qty-plus').addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      changeQuantity(variantId, +1, wrapper);
    });

    return wrapper;
  }

  // Change quantity via Shopify cart API
  async function changeQuantity(variantId, delta, wrapperElement) {
    if (wrapperElement) wrapperElement.classList.add('is-loading');

    try {
      // Get current cart to find the line number for this variant
      const cart = await fetchCart();
      if (!cart) return;

      let lineIndex = -1;
      let currentQty = 0;
      for (let i = 0; i < cart.items.length; i++) {
        if (cart.items[i].variant_id === variantId) {
          lineIndex = i + 1; // Shopify lines are 1-indexed
          currentQty = cart.items[i].quantity;
          break;
        }
      }

      if (lineIndex === -1 && delta > 0) {
        // Item not in cart but user clicked + — add it
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: 1 })
        });
      } else if (lineIndex !== -1) {
        const newQty = Math.max(0, currentQty + delta);
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ line: lineIndex, quantity: newQty })
        });
      }

      // Trigger theme's cart refresh so drawer updates too
      document.dispatchEvent(new CustomEvent('cart:refresh'));

      // Re-sync all buttons on the page
      await syncAllButtons();
    } catch (error) {
      console.error('[CustomATC] Error changing quantity:', error);
    } finally {
      if (wrapperElement) wrapperElement.classList.remove('is-loading');
    }
  }

  // Find the variant ID associated with a button
  function getVariantIdForButton(button) {
    // Look in the parent form for the variant input
    const form = button.closest('form');
    if (form) {
      const input = form.querySelector('input[name="id"]');
      if (input && input.value) {
        return parseInt(input.value, 10);
      }
    }
    return null;
  }

  // Transform a single button into quantity selector (or back)
  function transformButton(button, cartQtyMap) {
    if (!button) return;

    // Skip disabled buttons (sold out, etc.)
    if (button.hasAttribute('disabled')) return;

    // Skip buttons that aren't actually visible — UNLESS we're the ones
    // who hid them (custom-atc-hidden). card-product-list.liquid renders
    // TWO .product-card__atc buttons per card; a genuinely-hidden one
    // (never transformed) should still be skipped to avoid duplicate
    // selectors. But a button WE hid still has a live qty selector beside
    // it whose number needs syncing — so let those through.
    if (button.offsetParent === null &&
        button.getAttribute('data-custom-atc-hidden') !== 'true') {
      return;
    }

    const variantId = getVariantIdForButton(button);
    if (!variantId) return;

    const qty = cartQtyMap.get(variantId) || 0;

    // Find existing selector sibling (if any)
    const parent = button.parentNode;
    if (!parent) return;
    const existingSelector = parent.querySelector(
      '.custom-atc-qty-selector[data-variant-id="' + variantId + '"]'
    );

    if (qty > 0) {
      // Need to show quantity selector
      if (existingSelector) {
        // Just update the number
        const valueEl = existingSelector.querySelector('.custom-atc-qty-value');
        if (valueEl) valueEl.textContent = qty;
      } else {
        // Create selector and hide button
        const selector = buildQuantitySelector(variantId, qty, button);
        button.classList.add('custom-atc-hidden');
        button.setAttribute('data-custom-atc-hidden', 'true');
        parent.insertBefore(selector, button.nextSibling);
      }
    } else {
      // Need to show red button
      if (existingSelector) {
        existingSelector.remove();
      }
      if (button.getAttribute('data-custom-atc-hidden') === 'true') {
        button.classList.remove('custom-atc-hidden');
        button.removeAttribute('data-custom-atc-hidden');
      }
    }
  }

  // Find all add-to-cart buttons on the page and sync them
  async function syncAllButtons() {
    const cart = await fetchCart();
    if (!cart) return;
    const cartQtyMap = buildCartQuantityMap(cart);

    // Product page main buttons
    document.querySelectorAll('.product-form__submit').forEach(function (btn) {
      transformButton(btn, cartQtyMap);
    });

    // Product card quick-add buttons (only when they're real add-to-cart forms)
    document.querySelectorAll('.product-card__atc').forEach(function (btn) {
      // Skip if it's a link (Choose Options) — only transform real buttons
      if (btn.tagName.toLowerCase() !== 'button') return;
      transformButton(btn, cartQtyMap);
    });

    // Product card list-view buttons
    document.querySelectorAll('.product-card__list-atc').forEach(function (btn) {
      if (btn.tagName.toLowerCase() !== 'button') return;
      transformButton(btn, cartQtyMap);
    });
  }

  // Initialize
  waitForFoxTheme(function () {
    // Initial sync on page load
    syncAllButtons();

    // Re-sync whenever cart updates
    if (window.FoxTheme.pubsub && window.FoxTheme.pubsub.subscribe) {
      window.FoxTheme.pubsub.subscribe(
        window.FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
        function () {
          setTimeout(syncAllButtons, 100);
        }
      );
    }

    // Also sync after AJAX add-to-cart events
    document.addEventListener('product-ajax:added', function () {
      setTimeout(syncAllButtons, 100);
    });

    // Sync when cart-refresh event fires
    document.addEventListener('cart:refresh', function () {
      setTimeout(syncAllButtons, 500);
    });

    // Re-sync when page becomes visible (user comes back to tab)
    document.addEventListener('visibilitychange', function () {
      if (!document.hidden) {
        syncAllButtons();
      }
    });
  });

  // Also handle dynamically-added products (infinite scroll, etc.)
  const observer = new MutationObserver(function (mutations) {
    let shouldSync = false;
    mutations.forEach(function (mutation) {
      mutation.addedNodes.forEach(function (node) {
        if (node.nodeType === 1) {
          if (node.querySelector &&
              (node.querySelector('.product-form__submit') ||
               node.querySelector('.product-card__atc') ||
               node.querySelector('.product-card__list-atc'))) {
            shouldSync = true;
          }
        }
      });
    });
    if (shouldSync) {
      setTimeout(syncAllButtons, 200);
    }
  });

  if (document.body) {
    observer.observe(document.body, { childList: true, subtree: true });
  } else {
    document.addEventListener('DOMContentLoaded', function () {
      observer.observe(document.body, { childList: true, subtree: true });
    });
  }
})();
